import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { useSaveSettings, VectorTile } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { deleteVectorTileInSettings } from '../../VectorTiles/useVectorTileSettings';

interface VectorTileDeleteServiceProps {
  vectorTile: VectorTile;
}

export const VectorTileDeleteService = ({
  vectorTile,
}: VectorTileDeleteServiceProps) => {
  const { messages } = useLocalization();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { toast } = useToast();
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const settingsToSave = deleteVectorTileInSettings(vectorTile, settings);

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        setIsSubmitting(false);
        setShowDialog(false);
        toast({
          className: 'delete-vector-tile-service-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsVectorTileSuccessDeleteMessage,
        });
      },
      onError: () => {
        setIsSubmitting(false);
        setShowDialog(false);
        toast({
          className: 'delete-vector-tile-layer-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsVectorTileFailedDeleteMessage,
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="text-primary hover:text-primary">
          {messages.settingsVectorTileDeleteButtonText}
        </Button>
      </DialogTrigger>
      <DialogContent id="delete-wms-service-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.settingsVectorTileDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.settingsVectorTileDeleteWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.settingsVectorTileDeleteNameLabel}
            </Label>
            <Label htmlFor="vector-tile-name">{vectorTile.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              variant="destructive"
              type="submit"
              id="vector-tile-service-confirm-delete-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
